import { Application, Color, Container, Graphics } from 'pixi.js';
import {
    BaseWidget,
    WidgetPosition,
} from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';
import { AltitudeTapeWidget } from 'ff/debrief/components/hud/widgets/AltitudeTapeWidget';
import {
    RollingNumberConfig,
    RollingNumberWidget,
} from 'ff/debrief/components/hud/widgets/RollingNumberWidget';
import { VerticalSpeedTapeWidget } from 'ff/debrief/components/hud/widgets/VerticalSpeedTapeWidget';

export class AltitudeSection extends BaseWidget {
    static readonly requiredProperties = ['altitudeFeet'] as const;
    static readonly optionalProperties = [] as const;

    width: number;
    height: number;
    posX: number;
    posY: number;

    private altitudeTape: AltitudeTapeWidget;
    private currentAltitude: RollingNumberWidget;
    private readonly currentAltitudePosition = {
        x: 0,
        y: 0.46,
        width: 0.75,
        height: 0.08,
    };

    private readonly vsTapePosition = {
        x: 0.5,
        y: 0,
        width: 0.5,
        height: 1,
    };
    private vsTape: VerticalSpeedTapeWidget;

    override async init(
        pixiApp: Application,
        hudContext: HudContext,
        position: WidgetPosition,
        config?: Record<string, never>
    ): Promise<void> {
        await super.init(pixiApp, hudContext, position, config);

        this.altitudeTape = new AltitudeTapeWidget();
        await this.altitudeTape.init(
            pixiApp,
            hudContext,
            this.positionTranslator.translate({
                x: 0,
                y: 0,
                width: 1,
                height: 1,
            }),
            {},
            this.elementContainer
        );

        const currentAltitudeConfig: RollingNumberConfig = {
            dataProperty: 'altitudeFeet',
            precision: 10,
            fontSize: 12,
            fontColor: 'white',
            backgroundColor: '0x00000080',
        };
        const currentAltitudePosition: WidgetPosition =
            this.positionTranslator.translate(this.currentAltitudePosition);
        this.currentAltitude = new RollingNumberWidget();
        await this.currentAltitude.init(
            pixiApp,
            hudContext,
            currentAltitudePosition,
            currentAltitudeConfig,
            this.elementContainer
        );

        this.vsTape = new VerticalSpeedTapeWidget();
        await this.vsTape.init(
            pixiApp,
            hudContext,
            this.positionTranslator.translate(this.vsTapePosition),
            {},
            this.elementContainer
        );
    }

    override async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        await this.altitudeTape.populate();
        await this.currentAltitude.populate();
        await this.vsTape.populate();

        // place a semi-transparent background behind the entire section
        const background = new Graphics()
            .rect(
                positionTranslator.x(0),
                positionTranslator.y(0),
                positionTranslator.x(1),
                positionTranslator.y(1)
            )
            .fill(new Color([0.8, 0.8, 0.8, 0.2]));
        elementContainer.addChildAt(background, 0);
    }

    protected updateWidget(point: CurrentPoint, hudContext: HudContext): void {
        this.altitudeTape.update(point, hudContext);
        this.currentAltitude.update(point, hudContext);
        this.vsTape.update(point, hudContext);
    }

    cleanUp(): void {
        this.altitudeTape?.destroy();
        this.altitudeTape = null;

        this.currentAltitude?.destroy();
        this.currentAltitude = null;

        this.vsTape?.destroy();
        this.vsTape = null;
    }
}
